import type { Tile } from '@/types'

/**
 * Every project photograph on the site. The seven campaign photos in
 * `public/images/work/` are high resolution (1672 x 941) and also supply
 * cut-outs through `crop`; the rest are single high resolution images.
 */

const WIDTH = 1672
const HEIGHT = 941

type Range = [number, number]

/** A whole picture. `focus` is only set when given, since tiles travel through `getStaticProps`. */
const photo = (name: string, alt: string, focus?: Tile['focus']): Tile => ({
    src: `/images/work/${name}`,
    alt,
    ...(focus ? { focus } : {})
})

/** A cut-out of one of the seven campaign photos, in that photo's own pixels. */
const cut = (name: string, [x0, x1]: Range, [y0, y1]: Range, alt: string, focus?: Tile['focus']): Tile => ({
    ...photo(name, alt, focus),
    crop: { width: WIDTH, height: HEIGHT, x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
})

export const tiles = {
    aura: {
        campaign: photo('aura-skincare.png', 'AURA cream, toner, serum and carton arranged on pale stone'),
        trio: cut('aura-skincare.png', [545, 1335], [120, 910], 'AURA renewing cream, balancing toner and radiant serum on stone'),
        duo: cut('aura-skincare.png', [530, 1090], [230, 800], 'AURA renewing cream and balancing toner'),
        carton: cut('aura-skincare.png', [1100, 1672], [225, 654], 'AURA radiant serum beside its embossed carton'),
        portrait: photo('aura-portrait.jpg', 'AURA campaign portrait with the wordmark set in soft light', 'right'),
        card: photo('aura-card.jpg', 'AURA printed card with a pressed leaf'),
        bag: photo('aura-bag.jpg', 'AURA paper shopping bag in warm shadow'),
        editorial: photo('aura-editorial.jpg', 'AURA printed campaign cards among leaves'),
        sheet: photo('aura-sheet.jpg', 'AURA brand sheet with wordmark, colour palette and typeface')
    },
    kin: {
        campaign: photo('kin-coffee-bag.png', 'KIN coffee bag on stone beside a bowl of roasted beans', 'right'),
        pairing: photo('kin-bag-and-cup.png', 'KIN coffee bag and takeaway cup among roasted beans', 'right'),
        cover: cut('kin-coffee-bag.png', [620, 1561], [0, 941], 'KIN forest green coffee bag with an Ethiopia label'),
        wordmark: cut('kin-coffee-bag.png', [0, 836], [0, 941], 'KIN Coffee Roasters wordmark beside a bowl of beans'),
        cup: cut('kin-bag-and-cup.png', [1080, 1560], [290, 770], 'KIN takeaway cup on stone'),
        packaging: cut('kin-bag-and-cup.png', [640, 1560], [100, 900], 'KIN coffee bag and cup on stone'),
        sign: photo('kin-sign.jpg', 'KIN hanging sign outside the café'),
        menu: photo('kin-menu.jpg', 'KIN menu board on oak'),
        tote: photo('kin-tote.jpg', 'KIN green tote bag'),
        sheet: photo('kin-sheet.jpg', 'KIN brand sheet with wordmark, colour palette and typeface')
    },
    vanta: {
        campaign: photo('vanta-campaign.png', 'VANTA campaign portrait against concrete and sky', 'right'),
        figure: cut('vanta-campaign.png', [700, 1672], [0, 941], 'VANTA campaign portrait in a black coat and sunglasses'),
        wordmark: cut('vanta-campaign.png', [60, 840], [140, 720], 'VANTA F/W Collection wordmark against the sky'),
        label: photo('vanta-label.jpg', 'VANTA swing tag in black'),
        magazine: photo('vanta-magazine.jpg', 'VANTA lookbook cover'),
        hoodie: photo('vanta-hoodie.jpg', 'VANTA wordmark embroidered on a black hoodie'),
        poster: photo('vanta-poster.jpg', 'VANTA poster of a figure walking in black'),
        sign: photo('vanta-sign.jpg', 'VANTA illuminated sign on a dark facade'),
        sheet: photo('vanta-sheet.jpg', 'VANTA brand sheet with wordmark, colour palette and typeface')
    },
    arc: {
        campaign: photo('arc-house.png', 'ARC house on the cliffs above the sea at dusk', 'right'),
        house: cut('arc-house.png', [731, 1672], [0, 941], 'ARC concrete house lit at dusk'),
        wordmark: cut('arc-house.png', [100, 720], [190, 640], 'ARC Architecture Studio wordmark over the sea at dusk'),
        cards: photo('arc-cards.jpg', 'ARC blind embossed business cards'),
        monograph: photo('arc-monograph.jpg', 'ARC monograph spread titled Spaces That Inspire'),
        wall: photo('arc-wall.jpg', 'ARC wordmark on a concrete wall beside an olive tree'),
        interior: photo('arc-interior.jpg', 'ARC living room opening onto the hills'),
        tablet: photo('arc-tablet.jpg', 'ARC website shown on a tablet'),
        sheet: photo('arc-sheet.jpg', 'ARC brand sheet with wordmark, colour palette and typeface')
    },
    noma: {
        campaign: photo('noma-table.png', 'NOMA menu, linen and a plated course set at the restaurant entrance'),
        book: cut('noma-table.png', [260, 820], [200, 800], 'NOMA debossed menu cover in deep green'),
        linen: cut('noma-table.png', [630, 1080], [500, 840], 'NOMA card and stitched linen napkin'),
        entrance: cut('noma-table.png', [900, 1672], [0, 941], 'NOMA wordmark on the wall at the restaurant entrance'),
        dish: cut('noma-table.png', [0, 620], [540, 880], 'NOMA plated course in a stoneware bowl'),
        plate: photo('noma-plate.jpg', 'NOMA embossed ceramic plate'),
        menu: photo('noma-menu.jpg', 'NOMA printed menu on stone')
    },
    mono: {
        campaign: photo('mono-speaker.png', 'MONO speaker, packaging and headphones on rock above the sea at dusk', 'right'),
        speaker: cut('mono-speaker.png', [780, 1450], [250, 920], 'MONO speaker in soft black on dark rock'),
        box: cut('mono-speaker.png', [440, 980], [330, 740], 'MONO packaging and card in black'),
        wordmark: cut('mono-speaker.png', [80, 760], [130, 520], 'MONO Technology wordmark against the evening sky'),
        phones: photo('mono-phones.jpg', 'MONO app screens on two phones'),
        device: photo('mono-device.jpg', 'MONO device in soft black, close up'),
        wall: photo('mono-wall.jpg', 'MONO wordmark on a dark lobby wall')
    }
}
